import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, ChevronDown, ChevronUp, Dumbbell, Activity, ListFilter, Info } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EXERCISES } from '@/data/exercises';
import { Badge } from '@/components/ui/badge';
import { ExerciseInfoModal } from '@/components/ExerciseInfoModal';
import { Analytics } from '@/lib/analytics';

interface Exercise {
  id: string;
  name: string;
  sets: number;
  reps_min?: number;
  reps_max?: number;
  weight_kg?: number;
  rest_seconds?: number;
  notes?: string;
  muscle_groups: string[];
  exercise_type?: 'strength' | 'running' | 'generic';
}

interface WorkoutDay {
  id: string;
  dayNumber: number;
  title: string;
  description?: string;
  exercises: Exercise[];
  isExpanded: boolean;
}

interface CreateCustomProgramModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateCustomProgramModal = ({ isOpen, onClose, onSuccess }: CreateCustomProgramModalProps) => {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [selectedDayForExercise, setSelectedDayForExercise] = useState<string | null>(null);
  const [exerciseSearchQuery, setExerciseSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedExerciseInfoName, setSelectedExerciseInfoName] = useState<string>('');
  
  const [programData, setProgramData] = useState({
    title: '',
    description: '',
    duration_weeks: 4,
    program_type: 'strength'
  });

  const [workoutDays, setWorkoutDays] = useState<WorkoutDay[]>([
    {
      id: '1',
      dayNumber: 1,
      title: 'Day 1',
      description: '',
      exercises: [],
      isExpanded: true
    }
  ]);

  const addWorkoutDay = () => {
    const newDay: WorkoutDay = {
      id: Date.now().toString(),
      dayNumber: workoutDays.length + 1,
      title: `Day ${workoutDays.length + 1}`,
      description: '',
      exercises: [],
      isExpanded: true
    };
    setWorkoutDays([...workoutDays, newDay]);
  };

  const removeWorkoutDay = (dayId: string) => {
    setWorkoutDays(workoutDays.filter(d => d.id !== dayId));
  };

  const toggleDayExpanded = (dayId: string) => {
    setWorkoutDays(workoutDays.map(d => 
      d.id === dayId ? { ...d, isExpanded: !d.isExpanded } : d
    ));
  };

  const updateWorkoutDay = (dayId: string, field: string, value: any) => {
    setWorkoutDays(workoutDays.map(d => 
      d.id === dayId ? { ...d, [field]: value } : d
    ));
  };

  const openExerciseSelector = (dayId: string) => {
    setSelectedDayForExercise(dayId);
    setShowExerciseSelector(true);
    setExerciseSearchQuery('');
    setSelectedCategory('all');
  };

  const addExerciseFromLibrary = (exerciseName: string, muscleGroups: string[]) => {
    if (!selectedDayForExercise) return;
    
    const newExercise: Exercise = {
      id: Date.now().toString(),
      name: exerciseName,
      sets: 3,
      reps_min: 8,
      reps_max: 12,
      weight_kg: undefined,
      rest_seconds: 90,
      notes: '',
      muscle_groups: muscleGroups,
      exercise_type: 'strength'
    };
    
    setWorkoutDays(workoutDays.map(d => 
      d.id === selectedDayForExercise ? { ...d, exercises: [...d.exercises, newExercise] } : d
    ));
    
    setShowExerciseSelector(false);
    setSelectedDayForExercise(null);
  };

  const addRunningWorkout = () => {
    if (!selectedDayForExercise) return;
    
    const newExercise: Exercise = {
      id: Date.now().toString(),
      name: 'Running',
      sets: 1,
      reps_min: undefined,
      reps_max: undefined,
      weight_kg: undefined,
      rest_seconds: undefined,
      notes: 'Distance and duration to be determined',
      muscle_groups: ['Legs', 'Cardio'],
      exercise_type: 'running'
    };
    
    setWorkoutDays(workoutDays.map(d => 
      d.id === selectedDayForExercise ? { ...d, exercises: [...d.exercises, newExercise] } : d
    ));
    
    setShowExerciseSelector(false);
    setSelectedDayForExercise(null);
  };

  const addGenericActivity = () => {
    if (!selectedDayForExercise) return;
    
    const newExercise: Exercise = {
      id: Date.now().toString(),
      name: '',
      sets: 1,
      reps_min: undefined,
      reps_max: undefined,
      weight_kg: undefined,
      rest_seconds: undefined,
      notes: '',
      muscle_groups: [],
      exercise_type: 'generic'
    };
    
    setWorkoutDays(workoutDays.map(d => 
      d.id === selectedDayForExercise ? { ...d, exercises: [...d.exercises, newExercise] } : d
    ));
    
    setShowExerciseSelector(false);
    setSelectedDayForExercise(null);
  };

  const removeExercise = (dayId: string, exerciseId: string) => {
    setWorkoutDays(workoutDays.map(d => 
      d.id === dayId ? { ...d, exercises: d.exercises.filter(e => e.id !== exerciseId) } : d
    ));
  };

  const updateExercise = (dayId: string, exerciseId: string, field: string, value: any) => {
    setWorkoutDays(workoutDays.map(d => 
      d.id === dayId ? {
        ...d,
        exercises: d.exercises.map(e => 
          e.id === exerciseId ? { ...e, [field]: value } : e
        )
      } : d
    ));
  };

  const handleSave = async () => {
    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    if (!programData.title.trim()) {
      toast.error('Please enter a program title');
      return;
    }

    if (workoutDays.length === 0) {
      toast.error('Please add at least one workout day');
      return;
    }

    // Validate that each day has exercises
    const daysWithoutExercises = workoutDays.filter(d => d.exercises.length === 0);
    if (daysWithoutExercises.length > 0) {
      toast.error('Each workout day must have at least one exercise');
      return;
    }

    // Validate that each exercise has a name
    for (const day of workoutDays) {
      const exercisesWithoutName = day.exercises.filter(e => !e.name.trim());
      if (exercisesWithoutName.length > 0) {
        toast.error(`Day ${day.dayNumber}: All exercises must have a name`);
        return;
      }
    }

    setSaving(true);

    try {
      // Create the program
      const { data: program, error: programError } = await supabase
        .from('workout_programs')
        .insert({
          user_id: user.id,
          title: programData.title,
          description: programData.description,
          duration_weeks: programData.duration_weeks,
          program_type: programData.program_type,
          status: 'draft',
          created_from_chat: false
        })
        .select()
        .single();

      if (programError) throw programError;

      // Create workout days
      for (const day of workoutDays) {
        const { data: programDay, error: dayError } = await supabase
          .from('workout_program_days')
          .insert({
            program_id: program.id,
            day_number: day.dayNumber,
            workout_title: day.title,
            workout_description: day.description,
            estimated_duration_minutes: day.exercises.length * 5 // Rough estimate
          })
          .select()
          .single();

        if (dayError) throw dayError;

        // Create exercises for this day
        const exercisesToInsert = day.exercises.map((exercise, index) => ({
          program_day_id: programDay.id,
          exercise_name: exercise.name,
          exercise_order: index + 1,
          sets: exercise.sets,
          reps_min: exercise.reps_min,
          reps_max: exercise.reps_max,
          weight_kg: exercise.weight_kg,
          rest_seconds: exercise.rest_seconds,
          notes: exercise.notes,
          muscle_groups: exercise.muscle_groups.length > 0 ? exercise.muscle_groups : null
        }));

        const { error: exercisesError } = await supabase
          .from('workout_program_exercises')
          .insert(exercisesToInsert);

        if (exercisesError) throw exercisesError;
      }

      Analytics.programCreated('custom');
      toast.success('Custom program created successfully!');
      onSuccess?.();
      handleClose();
    } catch (error: any) {
      console.error('Error creating program:', error);
      toast.error(error.message || 'Failed to create program');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setProgramData({
      title: '',
      description: '',
      duration_weeks: 4,
      program_type: 'strength'
    });
    setWorkoutDays([
      {
        id: '1',
        dayNumber: 1,
        title: 'Day 1',
        description: '',
        exercises: [],
        isExpanded: true
      }
    ]);
    setShowExerciseSelector(false);
    setSelectedDayForExercise(null);
    onClose();
  };

  // Filter exercises based on search and category
  const filteredExercises = EXERCISES.filter(exercise => {
    const matchesSearch = exercise.name.toLowerCase().includes(exerciseSearchQuery.toLowerCase()) ||
                         exercise.muscle_groups.some(mg => mg.toLowerCase().includes(exerciseSearchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || exercise.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...new Set(EXERCISES.map(e => e.category))].sort();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Custom Workout Program</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Program Details */}
          <div className="space-y-4 p-4 bg-secondary/10 border-l-4 border-secondary rounded-lg">
            <h3 className="font-semibold text-secondary-foreground">Program Details</h3>
            
            <div>
              <Label htmlFor="title">Program Title *</Label>
              <Input
                id="title"
                value={programData.title}
                onChange={(e) => setProgramData({ ...programData, title: e.target.value })}
                placeholder="e.g., 12-Week Strength Builder"
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={programData.description}
                onChange={(e) => setProgramData({ ...programData, description: e.target.value })}
                placeholder="Describe your program goals and structure..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="duration">Duration (weeks)</Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  value={programData.duration_weeks}
                  onChange={(e) => setProgramData({ ...programData, duration_weeks: parseInt(e.target.value) || 1 })}
                />
              </div>

              <div>
                <Label htmlFor="type">Program Type</Label>
                <Select 
                  value={programData.program_type} 
                  onValueChange={(value) => setProgramData({ ...programData, program_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strength">Strength</SelectItem>
                    <SelectItem value="hypertrophy">Hypertrophy</SelectItem>
                    <SelectItem value="endurance">Endurance</SelectItem>
                    <SelectItem value="powerlifting">Powerlifting</SelectItem>
                    <SelectItem value="bodybuilding">Bodybuilding</SelectItem>
                    <SelectItem value="general">General Fitness</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Workout Days */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Workout Days</h3>
              <Button onClick={addWorkoutDay} size="sm" variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="h-4 w-4 mr-2" />
                Add Day
              </Button>
            </div>

            {workoutDays.map((day, dayIndex) => (
              <Collapsible key={day.id} open={day.isExpanded} onOpenChange={() => toggleDayExpanded(day.id)}>
                <div className="border-2 border-accent/40 rounded-lg overflow-hidden bg-accent/5">
                  <CollapsibleTrigger className="w-full p-4 bg-accent/10 hover:bg-accent/20 transition-colors border-b border-accent/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {day.isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                        <div className="text-left">
                          <div className="font-semibold">{day.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {day.exercises.length} exercise{day.exercises.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                      {workoutDays.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeWorkoutDay(day.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="p-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Day Title</Label>
                          <Input
                            value={day.title}
                            onChange={(e) => updateWorkoutDay(day.id, 'title', e.target.value)}
                            placeholder="e.g., Push Day, Legs, Upper Body"
                          />
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Input
                            value={day.description}
                            onChange={(e) => updateWorkoutDay(day.id, 'description', e.target.value)}
                            placeholder="Optional description"
                          />
                        </div>
                      </div>

                      {/* Exercises */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Exercises</Label>
                          <Button 
                            onClick={() => openExerciseSelector(day.id)} 
                            size="sm" 
                            className="bg-primary/90 text-primary-foreground hover:bg-primary"
                          >
                            <Plus className="h-3 w-3 mr-1" />
                            Add Exercise
                          </Button>
                        </div>

                        {day.exercises.length === 0 ? (
                          <div className="text-sm text-muted-foreground text-center py-4 bg-muted/30 rounded border border-dashed border-muted-foreground/30">
                            No exercises added yet
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {day.exercises.map((exercise, exerciseIndex) => (
                              <div key={exercise.id} className="p-3 bg-card border-2 border-muted rounded-lg space-y-3 hover:border-primary/50 transition-colors">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <Label className="text-xs">
                                      {exercise.exercise_type === 'generic' ? 'Activity Name *' : 'Exercise Name *'}
                                    </Label>
                                    <Input
                                      value={exercise.name}
                                      onChange={(e) => updateExercise(day.id, exercise.id, 'name', e.target.value)}
                                      placeholder={exercise.exercise_type === 'generic' ? 'e.g., Swimming, Cycling, Yoga' : 'e.g., Bench Press, Squat'}
                                      className="mt-1"
                                    />
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeExercise(day.id, exercise.id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>

                                {exercise.exercise_type !== 'generic' && exercise.exercise_type !== 'running' && (
                                  <>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div>
                                        <Label className="text-xs">Sets</Label>
                                        <Input
                                          type="number"
                                          min="1"
                                          value={exercise.sets}
                                          onChange={(e) => updateExercise(day.id, exercise.id, 'sets', parseInt(e.target.value) || 1)}
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs">Reps (Min)</Label>
                                        <Input
                                          type="number"
                                          min="1"
                                          value={exercise.reps_min || ''}
                                          onChange={(e) => updateExercise(day.id, exercise.id, 'reps_min', parseInt(e.target.value) || undefined)}
                                          placeholder="8"
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs">Reps (Max)</Label>
                                        <Input
                                          type="number"
                                          min="1"
                                          value={exercise.reps_max || ''}
                                          onChange={(e) => updateExercise(day.id, exercise.id, 'reps_max', parseInt(e.target.value) || undefined)}
                                          placeholder="12"
                                          className="mt-1"
                                        />
                                      </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <Label className="text-xs">Weight (kg)</Label>
                                        <Input
                                          type="number"
                                          step="0.5"
                                          value={exercise.weight_kg || ''}
                                          onChange={(e) => updateExercise(day.id, exercise.id, 'weight_kg', parseFloat(e.target.value) || undefined)}
                                          placeholder="Optional"
                                          className="mt-1"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs">Rest (seconds)</Label>
                                        <Input
                                          type="number"
                                          value={exercise.rest_seconds || ''}
                                          onChange={(e) => updateExercise(day.id, exercise.id, 'rest_seconds', parseInt(e.target.value) || undefined)}
                                          placeholder="90"
                                          className="mt-1"
                                        />
                                      </div>
                                    </div>
                                  </>
                                )}

                                <div>
                                  <Label className="text-xs">
                                    {exercise.exercise_type === 'generic' ? 'Details / Duration' : exercise.exercise_type === 'running' ? 'Distance & Duration' : 'Notes'}
                                  </Label>
                                  <Textarea
                                    value={exercise.notes || ''}
                                    onChange={(e) => updateExercise(day.id, exercise.id, 'notes', e.target.value)}
                                    placeholder={
                                      exercise.exercise_type === 'generic' 
                                        ? 'e.g., 30 minutes, moderate intensity' 
                                        : exercise.exercise_type === 'running'
                                        ? 'e.g., 5km in 30 minutes'
                                        : 'Optional notes or technique tips...'
                                    }
                                    rows={2}
                                    className="mt-1"
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-border/50">
            <Button onClick={handleClose} variant="outline" className="flex-1 border-muted-foreground/30">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90">
              {saving ? 'Creating...' : 'Create Program'}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Exercise Selector Dialog */}
      <Dialog open={showExerciseSelector} onOpenChange={(open) => {
        setShowExerciseSelector(open);
        if (!open) setSelectedDayForExercise(null);
      }}>
        <DialogContent className="max-w-3xl max-h-[80vh] p-2 sm:p-6">
          <DialogHeader className="pb-1 sm:pb-4">
            <DialogTitle className="text-base sm:text-xl">Add Exercise</DialogTitle>
          </DialogHeader>

          <div className="space-y-2 sm:space-y-4">
            {/* Quick Action Buttons */}
            <div className="grid grid-cols-3 gap-1.5 sm:gap-3">
              <Button
                variant="outline"
                className="h-auto py-1.5 sm:py-3 flex flex-col items-center gap-0.5 sm:gap-2 border-2 border-accent/60 hover:bg-accent/10 hover:border-accent px-1 sm:px-4"
                onClick={addRunningWorkout}
              >
                <Activity className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
                <div className="text-[10px] sm:text-sm font-medium">Running</div>
              </Button>
              <Button
                variant="outline"
                className="h-auto py-1.5 sm:py-3 flex flex-col items-center gap-0.5 sm:gap-2 border-2 border-accent/60 hover:bg-accent/10 hover:border-accent px-1 sm:px-4"
                onClick={addGenericActivity}
              >
                <ListFilter className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
                <div className="text-[10px] sm:text-sm font-medium">Activity</div>
              </Button>
              <Button
                variant="default"
                className="h-auto py-1.5 sm:py-3 flex flex-col items-center gap-0.5 sm:gap-2 bg-primary hover:bg-primary/90 px-1 sm:px-4"
                onClick={() => {}}
              >
                <Dumbbell className="h-3.5 w-3.5 sm:h-5 sm:w-5" />
                <div className="text-[10px] sm:text-sm font-medium">Library</div>
              </Button>
            </div>

            {/* Exercise Library Search */}
            <div className="space-y-1.5 sm:space-y-3">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-1.5 sm:gap-2">
                <Input
                  placeholder="Search..."
                  value={exerciseSearchQuery}
                  onChange={(e) => setExerciseSearchQuery(e.target.value)}
                  className="flex-1 text-xs sm:text-sm h-8 sm:h-10"
                />
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="w-full sm:w-[150px] text-xs sm:text-sm h-8 sm:h-10">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat} className="capitalize text-xs sm:text-sm">
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Exercise List */}
              <ScrollArea className="h-[220px] sm:h-[400px] border-2 border-muted rounded-md bg-muted/10">
                <div className="p-0.5 sm:p-2 space-y-0.5 sm:space-y-1">
                  {filteredExercises.length === 0 ? (
                    <div className="text-center py-4 sm:py-8 text-muted-foreground text-xs sm:text-sm">
                      No exercises found
                    </div>
                  ) : (
                    filteredExercises.map(exercise => (
                      <div key={exercise.id} className="flex items-stretch gap-0.5 sm:gap-1">
                        <Button
                          variant="ghost"
                          className="flex-1 justify-start h-auto py-1.5 sm:py-3 px-1.5 sm:px-3 hover:bg-accent/20 hover:border-l-4 hover:border-l-primary transition-all"
                          onClick={() => addExerciseFromLibrary(exercise.name, exercise.muscle_groups)}
                        >
                          <div className="flex flex-col items-start gap-0.5 sm:gap-1 w-full">
                            <div className="font-medium text-xs sm:text-sm truncate max-w-full">{exercise.name}</div>
                            <div className="flex flex-wrap gap-0.5 sm:gap-1">
                              {exercise.muscle_groups.slice(0, 2).map((mg, idx) => (
                                <Badge key={idx} variant="secondary" className="text-[9px] sm:text-xs px-1 py-0">
                                  {mg}
                                </Badge>
                              ))}
                            </div>
                            {exercise.equipment && (
                              <div className="text-[9px] sm:text-xs text-muted-foreground truncate max-w-full">
                                {exercise.equipment}
                              </div>
                            )}
                          </div>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="px-1 sm:px-2 hover:bg-accent/20 h-auto py-1.5"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedExerciseInfoName(exercise.name);
                          }}
                        >
                          <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exercise Info Modal */}
      <ExerciseInfoModal
        isOpen={!!selectedExerciseInfoName}
        onClose={() => setSelectedExerciseInfoName('')}
        exerciseName={selectedExerciseInfoName}
      />
    </Dialog>
  );
};